// dependencies
import { Link, useNavigate, useParams } from "react-router-dom"

// components
import DeleteProject from "./DeleteProject"
import PrimaryBtn from "../../components/btns/PrimaryBtn"
import ImageCarousel from "../../components/image/ImageCarousel"
import Tag from "../../components/ui/Tag"

// icons
import { FiCalendar } from "react-icons/fi";
import { FaCheckCircle } from "react-icons/fa";
import { FaGithub } from "react-icons/fa";
import { TbLivePhotoFilled } from "react-icons/tb";

// contexts
import { useAuth } from "../../contexts/AuthContext"

// hooks
import { useFetchProjectById } from "../../hooks/useFirebase"

// util
import { getDevelopmentPeriodString } from "../../util/format"

// style
import styles from "./css/Project.module.css"
import BB8LoadingPage from "../../components/layout/BB8LoadingPage";


const tagColors = {
    "Personal Project": { bg: "#232338", text: "#fff" },
    "Completed": { bg: "#4f3d73", text: "#fff" },
    "Freelance Project": { bg: "#97119c", text: "#fff" },
    "Uni Project": { bg: "#af3d73", text: "#fff" },
    "Active": { bg: "#60b1cc", text: "#fff" },
};


const Project = () => {
    const { id } = useParams()
    const { currentUser } = useAuth()
    const navigate = useNavigate();

    // fetch project from ID in url
    const { project, loading, error } = useFetchProjectById(id)
    // console.log(project)

    // calc development period
    const developmentPeriod = getDevelopmentPeriodString(project.dateStarted, project.dateCompleted);

    
    // handle edit project btn click
    const handleEditProject = () => {
        navigate(`/projects/edit/${id}`)
    }


    if (loading) return <><BB8LoadingPage /></>;
    if (error) return <p>Error: {error}</p>;
    if (!project) return <p>Project not found.</p>;


    return (
        <div className={styles.projectPageContainer}>
            <div className={styles.contentContainer}>
                {/* header */}
                <div className={styles.header}>
                    <h1 className={styles.title}>{project.title}</h1>
                    <div className={styles.tagsAndDate}>
                        {/* tags */}
                        <div className={styles.tags}>
                            {project.tags.map((tag, index) => {
                                const { bg, text } = tagColors[tag] || tagColors.Default;
                                return (
                                    <Tag 
                                        key={index} 
                                        text={tag} 
                                        fontSize="0.8rem" 
                                        backgroundColor={bg} 
                                        color={text} 
                                    />
                                );
                            })}
                        </div>
                        {/* development period */}
                        <div className={styles.developmentPeriod}>
                            <FiCalendar />
                            {developmentPeriod}
                        </div>
                    </div>
                </div>


                {/* images and yt video */}
                <div className={styles.imageCarousel}>
                    <ImageCarousel imgs={project.imgs} ytLink={project.ytLink} />
                </div>


                {/* content */}
                <div className={styles.projectInfo}>
                    <div className={styles.mainInfo}>
                        {/* about the project */}
                        <div className={styles.infoContainer}>
                            <div className={styles.sectionHeader}>About The Project</div>
                            <div className={styles.contentText} style={{ whiteSpace: "pre-line" }}>{project.content}</div>
                        </div>
                        {/* features */}
                        <div className={styles.infoContainer}>
                            <div className={styles.sectionHeader}>Features</div>
                            <div className={styles.features}>
                                {project.features.map((feature, index) => (
                                    <div key={index} className={styles.feature}>
                                        <div className={styles.featureIconContainer}>
                                            <FaCheckCircle color="#45a1bf" className={styles.featureIcon} />
                                        </div>
                                        <p key={index} className={styles.featureText}>{feature}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className={styles.otherInfo}>
                        {/* technologies */}
                        <div className={styles.infoContainer}>
                            <div className={styles.sectionHeader}>Technologies</div>
                            <div className={styles.technologies}>
                                {project.technologies.map((category, index) => (
                                    <div key={index} className={styles.techCategory}>
                                        <div className={styles.techType}>{category.type}</div>
                                        <ul className={styles.techItems}>
                                            {category.items.map((item, idx) => (
                                                <li key={idx} className={styles.techItem}>
                                                    {item}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        </div>
                        {/* links */}
                        <div className={styles.infoContainer}>
                            <div className={styles.sectionHeader}>Links</div>
                            { project.githubLink && (
                                <Link className={styles.githubLink} to={project.githubLink} target="_blank" rel="noopener noreferrer">
                                    <div className={styles.githubLinkContainer}>
                                        <div className={styles.githubIcon}><FaGithub size={20} /></div>
                                        <div className={styles.githubText}>Github Repository</div>
                                    </div>
                                </Link>
                            )}
                            { project.liveLink && (
                                <Link className={styles.githubLink} to={project.liveLink} target="_blank" rel="noopener noreferrer">
                                    <div className={styles.githubLinkContainer}>
                                        <div className={styles.githubIcon}><TbLivePhotoFilled size={20} /></div>
                                        <div className={styles.githubText}>Live Project</div>
                                    </div>
                                </Link>
                            )}
                        </div>
                    </div>
                </div>


                {/* edit and delete button */}
                {currentUser && 
                    <div className={styles.adminContainer}>
                        <PrimaryBtn text={"Edit Project"} onClick={handleEditProject} />
                        <DeleteProject id={id} projectTitle={project.title} />
                    </div>
                }
            </div>
        </div>
    )
}

export default Project
